import React from 'react';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  MaterialImportWithWarehousesDto,
  IWarehouse,
} from 'shared';
import apiClient from '../api/client';
import { useLanguage } from '../context/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';
import UserMenu from '../components/UserMenu';
import { useMaterials } from '../hooks/useMaterials';

interface Params {
  id: string;
}

interface FormValues {
  date: string;
  notes?: string;
  costPerUnit: number;
  transactions: Array<{
    warehouseId: string;
    quantity: number;
  }>;
}

const MaterialImport: React.FC = () => {
  const { id } = useParams<Params>();
  const history = useHistory();
  const { t } = useLanguage();
  const { listQuery, importMutation } = useMaterials();

  const material = listQuery.data?.find((item) => item.id === id);

  const warehousesQuery = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => {
      const { data } = await apiClient.get<IWarehouse[]>('/warehouses');
      return data;
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      costPerUnit: Number(material?.costPerUnit || 0),
      transactions: [{ warehouseId: '', quantity: 0 }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'transactions',
  });

  if (!id || !material) {
    history.replace('/tabs/materials');
    return null;
  }

  const onSubmit = async (values: FormValues) => {
    const validRows = values.transactions.filter(
      (row) => row.warehouseId && Number(row.quantity) > 0,
    );

    if (validRows.length === 0) {
      return;
    }

    const payload: MaterialImportWithWarehousesDto = {
      materialId: id,
      date: values.date,
      costPerUnit: Number(values.costPerUnit),
      notes: values.notes,
      warehouseImports: validRows.map((row) => ({
        warehouseId: row.warehouseId,
        quantity: Number(row.quantity),
      })),
    };

    await importMutation.mutateAsync(payload);
    history.replace(`/tabs/materials/${id}`);
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/tabs/materials/${id}`} />
          </IonButtons>
          <IonTitle>{t('addImport') ?? 'Add Import'}</IonTitle>
          <LanguageToggle />
          <UserMenu />
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="ion-padding">
        <form onSubmit={handleSubmit(onSubmit)}>
          <IonList>
            <IonItem>
              <IonLabel position="stacked">{t('date')}</IonLabel>
              <Controller
                control={control}
                name="date"
                rules={{ required: true }}
                render={({ field }) => (
                  <IonInput
                    type="date"
                    value={field.value}
                    onIonChange={(event) => field.onChange(event.detail.value ?? '')}
                  />
                )}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">{t('costPerUnit')}</IonLabel>
              <Controller
                control={control}
                name="costPerUnit"
                rules={{ required: true, min: 0 }}
                render={({ field }) => (
                  <IonInput
                    type="number"
                    value={field.value?.toString() ?? ''}
                    onIonChange={(event) => field.onChange(Number(event.detail.value ?? 0))}
                  />
                )}
              />
              {errors.costPerUnit ? (
                <IonText color="danger">{t('enterCostPerUnit') ?? 'Enter cost per unit'}</IonText>
              ) : null}
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">{t('notes')}</IonLabel>
              <Controller
                control={control}
                name="notes"
                render={({ field }) => (
                  <IonInput
                    value={field.value ?? ''}
                    onIonChange={(event) => field.onChange(event.detail.value ?? '')}
                  />
                )}
              />
            </IonItem>
          </IonList>

          <IonText color="primary">
            <h2 style={{ marginTop: 24 }}>{t('warehouseDistribution')}</h2>
          </IonText>

          <IonList>
            {fields.map((field, index) => (
              <IonItem key={field.id} lines="full">
                <IonLabel position="stacked">{t('warehouse')}</IonLabel>
                <Controller
                  control={control}
                  name={`transactions.${index}.warehouseId`}
                  rules={{ required: true }}
                  render={({ field: warehouseField }) => (
                    <IonSelect
                      value={warehouseField.value}
                      placeholder={t('selectWarehouse') ?? 'Select warehouse'}
                      onIonChange={(event) => warehouseField.onChange(event.detail.value)}
                    >
                      {warehousesQuery.data?.map((warehouse) => (
                        <IonSelectOption key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  )}
                />

                <IonLabel position="stacked">{t('quantity')}</IonLabel>
                <Controller
                  control={control}
                  name={`transactions.${index}.quantity`}
                  rules={{ required: true, min: 0 }}
                  render={({ field: quantityField }) => (
                    <IonInput
                      type="number"
                      value={quantityField.value?.toString() ?? ''}
                      onIonChange={(event) => quantityField.onChange(Number(event.detail.value ?? 0))}
                    />
                  )}
                />

                <IonButtons slot="end">
                  <IonButton
                    type="button"
                    color="danger"
                    onClick={() => remove(index)}
                    disabled={fields.length === 1}
                  >
                    {t('removeRow') ?? 'Remove'}
                  </IonButton>
                </IonButtons>
              </IonItem>
            ))}
          </IonList>

          <IonButton
            expand="block"
            type="button"
            onClick={() => append({ warehouseId: '', quantity: 0 })}
            style={{ marginTop: 16 }}
          >
            {t('addWarehouseRow') ?? 'Add Warehouse'}
          </IonButton>

          <IonButton
            expand="block"
            type="submit"
            disabled={isSubmitting}
            style={{ marginTop: 24 }}
          >
            {isSubmitting ? (t('saving') ?? 'Saving…') : t('save')}
          </IonButton>
        </form>
      </IonContent>
    </IonPage>
  );
};

export default MaterialImport;
